import { StrUploadResultStatus as Status, StrUploadResult } from "@/models/responses";
import React from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/shadcn/ui/table";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useSetReservationStrStatusMutation } from "@/store/booking-management-api";
import { useGetTaskStatusQuery } from "@/store/str-api";

interface ShortTermStayUploadProgressProps {
    taskId: number;
    onComplete: () => void;
}

function mapApiStatusToStatus(apiStatus: string): Status {
    switch (apiStatus.toUpperCase()) {
        case "PENDING":
            return Status.Pending;
        case "SUCCESS":
            return Status.Success;
        default:
            return Status.Error;
    }
}

const ShortTermStayUploadProgress: React.FC<ShortTermStayUploadProgressProps> = ({ taskId, onComplete }) => {
    // Use RTK Query with polling enabled
    const {
        data: taskStatusResponse,
        error,
        isLoading,
    } = useGetTaskStatusQuery(taskId.toString(), {
        skip: taskId === -1,
        pollingInterval: 5000, // Poll every 5 seconds
        refetchOnMountOrArgChange: true,
    });
    const [setReservationStrStatus] = useSetReservationStrStatusMutation();

    // Map API response to your format
    const uploadResults: StrUploadResult[] = React.useMemo(() => {
        if (!taskStatusResponse) return [];

        return taskStatusResponse.strUploadStatus.reservationUploadStatus.map((status) => {
            const mappedStatus = mapApiStatusToStatus(status.status);
            const reservationId = status.reservationId.split("-")[1];
            if (mappedStatus === "success" || (mappedStatus === "error" && status.details.includes("duplicate declaration"))) {
                setReservationStrStatus({
                    uploaded: true,
                    reservationId,
                })
                    .unwrap()
                    .catch((error) => {
                        console.error("Failed to set STR status:", error);
                    });
            }
            return {
                reservationCode: status.reservationId,
                status: mappedStatus,
                text: status.details || "",
            };
        });
    }, [taskStatusResponse]);

    // Check if all tasks are complete
    React.useEffect(() => {
        if (!uploadResults.length) return;

        const allCompleted = uploadResults.every((result) => result.status !== Status.Pending);

        if (allCompleted) {
            onComplete();
        }
    }, [uploadResults, onComplete]);

    // Helper function to map API status to your Status enum

    const StatusIcon = ({ status }: { status: Status }) => {
        switch (status) {
            case Status.Pending:
                return <Loader2 className="h-5 w-5 animate-spin text-blue-500" />;
            case Status.Success:
                return <CheckCircle2 className="h-5 w-5 text-green-500" />;
            default:
                return <XCircle className="h-5 w-5 text-red-500" />;
        }
    };

    // Show loading state while initially fetching
    if (isLoading && uploadResults.length === 0) {
        return (
            <div className="flex justify-center py-4">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    // Handle error state
    if (error) {
        return <div className="text-red-500">Error loading upload status</div>;
    }

    return (
        <div className="relative">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[200px] font-bold">RESERVATION CODE</TableHead>
                        <TableHead className="w-[100px] font-bold">STATUS</TableHead>
                        <TableHead className="font-bold">DESCRIPTION</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {uploadResults.map((result) => (
                        <TableRow key={result.reservationCode}>
                            <TableCell className="font-medium">{result.reservationCode}</TableCell>
                            <TableCell>
                                <div className="flex items-center justify-center">
                                    <StatusIcon status={result.status} />
                                </div>
                            </TableCell>
                            <TableCell className="whitespace-normal wrap-break-word">{result.text}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

export default ShortTermStayUploadProgress;
